/**
 * Message labels — the row/reading-pane chips (`LabelChips`) and the per-message
 * apply/remove affordance (`LabelPickerPopover`). Labels are account-scoped and
 * many-per-message; applying replaces the message's full set via
 * `useApplyMessageLabels`. `LabelPickerPopover` also creates a label inline and
 * opens the full manager (rename/recolor/delete).
 */

import { useMemo, useState, type FormEvent } from 'react';
import { Tag, Plus, Loader2, Settings2 } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import type { EmailMessageLabelRefDto } from '@/lib/api';
import { useEmailLabels, useCreateEmailLabel, useApplyMessageLabels } from '../hooks/useMailbox';

const DEFAULT_NEW_COLOR = '#6B7280';

/**
 * Renders a message's labels as compact colored chips. When `onSelect` is given
 * each chip is a button that drills the list down to that label (row/reading
 * pane); otherwise the chips are static. Renders nothing when there are no labels.
 */
export function LabelChips({
  labels,
  onSelect,
  className,
}: {
  labels?: EmailMessageLabelRefDto[];
  onSelect?: (labelId: string) => void;
  className?: string;
}) {
  if (!labels || labels.length === 0) return null;
  return (
    <span className={`flex flex-wrap items-center gap-1 ${className ?? ''}`}>
      {labels.map((l) => {
        const inner = (
          <span
            className="inline-flex items-center gap-1 rounded-full border px-1.5 py-px text-[10px] font-medium leading-tight"
            style={{ borderColor: l.color, color: l.color }}
          >
            <span
              className="h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ backgroundColor: l.color }}
            />
            <span className="max-w-[10rem] truncate">{l.name}</span>
          </span>
        );
        return onSelect ? (
          <button
            key={l.labelId}
            type="button"
            title={`Filter by ${l.name}`}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(l.labelId);
            }}
          >
            {inner}
          </button>
        ) : (
          <span key={l.labelId}>{inner}</span>
        );
      })}
    </span>
  );
}

/**
 * Per-message label picker: toggle the account's labels on/off (each toggle
 * replaces the message's full set), create + apply a new one inline, and open
 * the full manager. Labels for the account load only while the popover is open.
 */
export function LabelPickerPopover({
  accountId,
  messageId,
  applied,
  onManage,
}: {
  accountId: string;
  messageId: string;
  applied: EmailMessageLabelRefDto[];
  onManage: () => void;
}) {
  const [open, setOpen] = useState(false);
  const { data: labels, isLoading } = useEmailLabels(accountId, open);
  const apply = useApplyMessageLabels();
  const create = useCreateEmailLabel();
  const [newName, setNewName] = useState('');

  const appliedIds = useMemo(() => new Set(applied.map((l) => l.labelId)), [applied]);

  const toggle = (labelId: string, on: boolean) => {
    const next = on
      ? [...appliedIds, labelId]
      : [...appliedIds].filter((existing) => existing !== labelId);
    apply.mutate({ messageId, accountId, labelIds: next });
  };

  const addAndApply = (e: FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    create.mutate(
      { accountId, name, color: DEFAULT_NEW_COLOR },
      {
        onSuccess: (res) => {
          setNewName('');
          apply.mutate({ messageId, accountId, labelIds: [...appliedIds, res.label.labelId] });
        },
      },
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 gap-1" title="Labels">
          <Tag className="h-3.5 w-3.5" /> Labels
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        <div className="max-h-56 space-y-0.5 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : !labels || labels.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              No labels yet — add one below.
            </p>
          ) : (
            labels.map((l) => {
              const on = appliedIds.has(l.labelId);
              return (
                <label
                  key={l.labelId}
                  className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-accent"
                >
                  <Checkbox
                    checked={on}
                    disabled={apply.isPending}
                    onCheckedChange={(checked) => toggle(l.labelId, checked)}
                    aria-label={`${on ? 'Remove' : 'Apply'} label ${l.name}`}
                  />
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: l.color }}
                  />
                  <span className="min-w-0 flex-1 truncate">{l.name}</span>
                </label>
              );
            })
          )}
        </div>

        {/* Create + apply a new label inline. */}
        <form onSubmit={addAndApply} className="mt-2 flex items-center gap-1.5 border-t pt-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New label"
            maxLength={60}
            aria-label="New label name"
            className="h-7 flex-1 text-sm"
          />
          <Button
            type="submit"
            size="sm"
            className="h-7 w-7 p-0"
            disabled={!newName.trim() || create.isPending}
            aria-label="Create and apply label"
          >
            {create.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Plus className="h-3.5 w-3.5" />
            )}
          </Button>
        </form>

        <Button
          variant="ghost"
          size="sm"
          className="mt-1 h-7 w-full justify-start gap-1.5 text-muted-foreground"
          onClick={() => {
            setOpen(false);
            onManage();
          }}
        >
          <Settings2 className="h-3.5 w-3.5" /> Manage labels
        </Button>
      </PopoverContent>
    </Popover>
  );
}
